import {
  BudgetEntry,
  EnrichedBudgetEntry,
  SettlementStatus,
} from 'src/Components/Expenses/BudgetList/BudgetList.types';
import { SettlementTargetDto, isPersonTarget } from 'src/types/money-transfer.types';
import { getMainCategoryForType } from 'src/Utils/expenseCategories';

/**
 * Labels the enrichment needs but cannot resolve on its own: they come from i18n, which
 * keeps every helper here pure and independent of the React tree.
 */
export interface BudgetEntryLabels {
  /** Displayed title of a money transfer row, e.g. "Remboursement". */
  transferTitle: string;
  /** Translated label of an expense type, e.g. ELECTRICITY -> "Électricité". */
  categoryLabel: (type: string) => string;
  /** Fallback label for a share user whose firstName/lastName are both empty. */
  invitedUserLabel: string;
}

// A user target can have an empty firstName/lastName (invited member who hasn't completed
// their profile yet); the backend has no other name to fall back on than the target name.
const settlementTargetName = (target: SettlementTargetDto): string =>
  isPersonTarget(target)
    ? (([target.firstName, target.lastName].filter(Boolean).join(' ') || target.name) ?? '')
    : target.name;

/**
 * Who advanced the money. Members, bank accounts, external contacts and placeholder
 * members are all eligible, so the payer filter covers every row the table can show.
 *
 * On an income the "payer" is not `paidBy` (which is the collector, i.e. where the money
 * landed) — it is the sole share owner: the fund-call pattern gives that member 100% of
 * the share (see ExpenseForm fund-call block). No single-user share ⇒ no payer.
 */
export const computePayerName = (
  entry: BudgetEntry,
  labels: BudgetEntryLabels
): { id: string; name: string } | null => {
  if (entry.kind === 'transfer') {
    return { id: entry.from.id, name: settlementTargetName(entry.from) };
  }

  if (entry.isIncome) {
    const shares = entry.shares ?? [];
    if (shares.length !== 1) return null;
    const share = shares[0];
    if (!share.user) return null;
    const rawName = [share.user.firstName, share.user.lastName].filter(Boolean).join(' ');
    return { id: share.user.id, name: rawName.length > 0 ? rawName : labels.invitedUserLabel };
  }

  if (entry.paidBy) {
    return {
      id: entry.paidBy.id,
      name: [entry.paidBy.firstName, entry.paidBy.lastName].filter(Boolean).join(' '),
    };
  }
  if (entry.paidByBankAccount) {
    return { id: entry.paidByBankAccount.id, name: entry.paidByBankAccount.name };
  }
  if (entry.paidByContact) {
    return { id: entry.paidByContact.id, name: entry.paidByContact.displayName };
  }
  if (entry.paidByPlaceholderMember) {
    const name =
      [entry.paidByPlaceholderMember.firstName, entry.paidByPlaceholderMember.lastName]
        .filter(Boolean)
        .join(' ') || entry.paidByPlaceholderMember.firstName;
    return { id: entry.paidByPlaceholderMember.id, name };
  }

  return null;
};

/**
 * Who actually collected the money (paidBy on an income, or the same person who advanced
 * it on an expense). Kept separate from `computePayerName` because the "Pour moi" tooltip
 * still needs to say « Encaissé par X » for incomes even after the Payer column stops
 * showing the collector.
 */
export const computeCollectorName = (entry: BudgetEntry): string | null => {
  if (entry.kind === 'transfer') return null;
  if (entry.paidBy) {
    return [entry.paidBy.firstName, entry.paidBy.lastName].filter(Boolean).join(' ');
  }
  if (entry.paidByBankAccount) return entry.paidByBankAccount.name;
  if (entry.paidByContact) {
    return entry.paidByContact.displayName;
  }
  if (entry.paidByPlaceholderMember) {
    return (
      [entry.paidByPlaceholderMember.firstName, entry.paidByPlaceholderMember.lastName]
        .filter(Boolean)
        .join(' ') || entry.paidByPlaceholderMember.firstName
    );
  }
  return null;
};

/**
 * Settlement status of the whole row, not of one member's share: the model only carries
 * Expense.reimbursedAt (global). Per-member settlement is a separate card.
 */
export const computeSettlementStatus = (entry: BudgetEntry): SettlementStatus => {
  if (entry.kind === 'transfer') return 'NOT_APPLICABLE';
  if (entry.isVirtual) return 'NOT_APPLICABLE';
  if (entry.isReimbursed === undefined) return 'NOT_APPLICABLE';
  if (!entry.paidBy && !entry.paidByBankAccount) return 'NOT_APPLICABLE';
  if (entry.isReimbursed) return 'SETTLED';
  // Some participants paid, others have not: neither "en attente" nor "réglé".
  const settled = entry.settledSharesCount ?? 0;
  return settled > 0 ? 'PARTIAL' : 'PENDING';
};

/**
 * NOT_APPLICABLE maps to null rather than to a rank so the sort hook keeps those rows last
 * in BOTH directions, exactly like the "—" rows of the "Pour moi" column.
 */
export const computeStatusRank = (status: SettlementStatus): number | null => {
  if (status === 'SETTLED') return 3;
  if (status === 'PARTIAL') return 2;
  if (status === 'PENDING') return 1;
  return null;
};

/**
 * What the row costs (negative) or earns back (positive) for the current user, in
 * settlement terms rather than in cash terms:
 *
 * - expense  `paid - myShare`  — advanced 300 with a 100 share means 200 to recover
 * - income   `myShare - paid`  — collected 800 with a 200 share means 600 to redistribute
 * - transfer `+amount` received, `-amount` sent
 *
 * Returns null when the user is neither payer nor part of the shares: the row does not
 * concern them, which the table renders as "—" and not as "0 €".
 */
export const computeNetImpact = (entry: BudgetEntry, currentUserId?: string): number | null => {
  if (!currentUserId) return null;

  if (entry.kind === 'transfer') {
    if (entry.from.id === currentUserId) return -entry.amount;
    if (entry.to.id === currentUserId) return entry.amount;
    return null;
  }

  const myShare = entry.shares?.find(share => share.user?.id === currentUserId);
  const paidByMe = entry.paidBy?.id === currentUserId;
  if (!myShare && !paidByMe) return null;

  const myShareAmount = myShare?.amount ?? 0;
  const paidAmount = paidByMe ? entry.amount : 0;

  return entry.isIncome ? myShareAmount - paidAmount : paidAmount - myShareAmount;
};

/** Title used for sorting and searching; transfers carry no `title` of their own. */
export const computeSortTitle = (entry: BudgetEntry, labels: BudgetEntryLabels): string =>
  entry.kind === 'transfer' ? labels.transferTitle : entry.title;

/**
 * Pre-computes on every entry the values the DataTable can only sort on if they exist on
 * the record itself — the status and the personal impact used to be built inside the
 * column `render`, where `getValueAtPath` finds nothing to read.
 */
export const enrichBudgetEntry = (
  entry: BudgetEntry,
  currentUserId: string | undefined,
  labels: BudgetEntryLabels
): EnrichedBudgetEntry => {
  const payer = computePayerName(entry, labels);
  const status = computeSettlementStatus(entry);
  const isTransfer = entry.kind === 'transfer';

  return {
    ...entry,
    sortTitle: computeSortTitle(entry, labels),
    payerName: payer?.name ?? null,
    payerId: payer?.id ?? null,
    collectorName: computeCollectorName(entry),
    categoryLabel: isTransfer ? null : labels.categoryLabel(entry.type),
    mainCategory: isTransfer ? null : getMainCategoryForType(entry.type),
    sharesCount: isTransfer ? 0 : (entry.shares?.length ?? 0),
    status,
    statusRank: computeStatusRank(status),
    netImpact: computeNetImpact(entry, currentUserId),
  } as EnrichedBudgetEntry;
};
